import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Skeleton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import JsonDataModal from "../components/admin/JsonDataModal";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";

export default function SquarespacePrices() {
  const navigate = useNavigate();
  const api = useApi();
  const [pricingData, setPricingData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [typeFilter, setTypeFilter] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 50,
    total: 0,
    offset: 0,
  });
  const [paginationUrls, setPaginationUrls] = React.useState({
    nextPageUrl: null,
    previousPageUrl: null,
  });
  const [pageHistory, setPageHistory] = React.useState([]);
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  const fetchPricing = React.useCallback(async (nextPageUrlParam = null) => {
    setLoading(true);
    setError(null);
    try {
      const path = nextPageUrlParam
        ? `/admin/squarespace/prices?nextPageUrl=${encodeURIComponent(nextPageUrlParam)}`
        : "/admin/squarespace/prices";
      const responseData = await api.get(path);
      // API now returns { data: [...], pagination: {...}, nextPageUrl: ... }
      // Use the data array directly for the pricing table
      const data = responseData.data || [];
      const paginationInfo = responseData.pagination || {};
      
      setPricingData({
        data: data,
        pagination: paginationInfo,
      });
      
      // Update pagination URLs - check both root level and pagination object
      setPaginationUrls({
        nextPageUrl: responseData.nextPageUrl || paginationInfo.nextPageUrl || null,
        previousPageUrl: responseData.previousPageUrl || paginationInfo.previousPageUrl || null,
      });
      
      // Track page history for back navigation
      if (nextPageUrlParam && !pageHistory.includes(nextPageUrlParam)) {
        setPageHistory(prev => [...prev, nextPageUrlParam]);
      }
      
      // Update pagination state if pagination info exists
      if (paginationInfo) {
        setPagination({
          page: paginationInfo.page || 1,
          limit: paginationInfo.limit || data.length,
          total: paginationInfo.total || data.length,
          offset: paginationInfo.offset || 0,
        });
      } else {
        // If no pagination info, assume all data is shown
        setPagination({
          page: 1,
          limit: data.length,
          total: data.length,
          offset: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching pricing:", err);
      setError(err.message || "Failed to fetch pricing");
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const handleNextPage = () => {
    if (paginationUrls.nextPageUrl) {
      fetchPricing(paginationUrls.nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (paginationUrls.previousPageUrl) {
      fetchPricing(paginationUrls.previousPageUrl);
    } else if (pageHistory.length > 0) {
      // Go back in history
      const previousUrl = pageHistory[pageHistory.length - 1];
      setPageHistory(prev => prev.slice(0, -1));
      fetchPricing(previousUrl);
    } else {
      // Go back to first page (no nextPageUrl parameter)
      fetchPricing(null);
    }
  };

  // Helper to format currency
  const formatCurrency = (amount, currency = "USD") => {
    if (amount === undefined || amount === null) return "N/A";
    // Handle both cents and dollars - if amount > 10000, assume it's in cents
    const value = amount > 10000 ? amount / 100 : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  // Extract pricing items from data array
  // The API returns { data: [...], pagination: {...} }
  const allPricingItems = Array.isArray(pricingData?.data) ? pricingData.data : [];
  
  // Filter by type and name search
  const pricingItems = React.useMemo(() => {
    let filtered = allPricingItems;
    
    // Filter by type
    if (typeFilter) {
      filtered = filtered.filter((item) => {
        const itemType = item.type || item.category || (item.tld ? 'domain' : 'website');
        return itemType.toLowerCase() === typeFilter.toLowerCase();
      });
    }
    
    // Filter by name/search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const productName = item.productName || item.name || item.planName || item.tld || item.extension || '';
        const searchableText = productName.toLowerCase();
        return searchableText.includes(query);
      });
    }
    
    return filtered;
  }, [allPricingItems, typeFilter, searchQuery]);
  
  // Get unique types for the filter dropdown
  const availableTypes = React.useMemo(() => {
    const types = new Set();
    allPricingItems.forEach((item) => {
      const itemType = item.type || item.category || (item.tld ? 'domain' : 'website');
      if (itemType) {
        types.add(itemType.toLowerCase());
      }
    });
    return Array.from(types).sort();
  }, [allPricingItems]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Prices
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Reseller Pricing"
              description="View pricing for domains and website plans"
              icon={<AttachMoneyIcon sx={{ color: "primary.main" }} />}
            >
              {!loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CodeIcon />}
                    onClick={() => setJsonModalOpen(true)}
                  >
                    View JSON
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Page:
                  </Typography>
                  <Chip
                    label={pagination.page || 1}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Search by product name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Filter by Type</InputLabel>
                      <Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        label="Filter by Type"
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {availableTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Pagination controls above table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell>
                            <Skeleton height={20} />
                          </TableCell>
                          <TableCell>
                            <Skeleton height={24} width={80} borderRadius={4} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton height={20} width={100} />
                          </TableCell>
                          <TableCell>
                            <Skeleton height={20} width={80} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton height={32} width={80} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : pricingItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            No pricing data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pricingItems.map((item, index) => {
                        const productName = item.productName || item.name || item.planName || item.tld || item.extension || `Item ${index + 1}`;
                        const type = item.type || item.category || (item.tld ? 'domain' : 'website');
                        const price = item.price || item.monthlyPrice || item.annualPrice;
                        const frequency = item.frequency || item.billingCycle || item.billingFrequency || item.period || item.duration || (item.monthlyPrice ? 'Monthly' : item.annualPrice ? 'Annual' : 'N/A');

                        // Create a unique ID for navigation (use id, productName, or index)
                        const itemId = item.id || item.tld || item.extension || encodeURIComponent(productName) || index;

                        return (
                          <TableRow key={item.id || index} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {productName}
                              </Typography>
                              {(item.pricingId || item.id) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {item.pricingId || item.id}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={type}
                                size="small"
                                color={type === 'domain' ? 'primary' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {price ? formatCurrency(price) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {frequency}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/dashboard/squarespace/prices/${itemId}`)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

                  {/* Pagination controls below table */}
                  {(paginationUrls.nextPageUrl || paginationUrls.previousPageUrl || pageHistory.length > 0) && !loading && (
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        onClick={handlePreviousPage}
                        disabled={!paginationUrls.previousPageUrl && pageHistory.length === 0 || loading}
                      >
                        Previous Page
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {pagination.page || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleNextPage}
                        disabled={!paginationUrls.nextPageUrl || loading}
                      >
                        Next Page
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {!loading && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {typeFilter || searchQuery.trim() ? (
                    <>Showing {pricingItems.length} item{pricingItems.length !== 1 ? 's' : ''} (filtered from {allPricingItems.length} total)</>
                  ) : (
                    <>Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} item{pagination.total !== 1 ? 's' : ''}</>
                  )}
                </Typography>
                {pagination.total > pagination.limit && !typeFilter && !searchQuery.trim() && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        // TODO: Implement pagination API call if Squarespace API supports it
                        const newPage = pagination.page - 1;
                        if (newPage >= 1) {
                          setPagination({ ...pagination, page: newPage, offset: (newPage - 1) * pagination.limit });
                        }
                      }}
                      disabled={pagination.page <= 1 || loading}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        // TODO: Implement pagination API call if Squarespace API supports it
                        const newPage = pagination.page + 1;
                        const totalPages = Math.ceil(pagination.total / pagination.limit);
                        if (newPage <= totalPages) {
                          setPagination({ ...pagination, page: newPage, offset: (newPage - 1) * pagination.limit });
                        }
                      }}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit) || loading}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={{
          prices: allPricingItems,
          total: allPricingItems.length,
          pagination: pagination,
        }}
      />
    </>
  );
}
